import { createAsyncThunk, createEntityAdapter, createSlice } from '@reduxjs/toolkit';
import { ExtendedNoteModel, JSONApiDataObject, ReduxEntity } from 'Douleutaras/types';
import { makeRequest } from 'Douleutaras/services/apiClient';

const NOTES_EXTENDED_ENDPOINT = 'api/public/v3/outer-job';
const getEndpoint = (jobId: string | number) => `${NOTES_EXTENDED_ENDPOINT}/${jobId}/notes/`;

const notesAdapter = createEntityAdapter<JSONApiDataObject<ExtendedNoteModel>>({
  selectId: job => job?.id ?? job?.attributes?.id
});

// Get Notes Thunk
export const getNotes = createAsyncThunk<
  Array<JSONApiDataObject<ExtendedNoteModel>>,
  string | number,
  { rejectValue: { errors?: Array<string>, serverError?: string, error?: string } }
>(
  'pro/outerJobNotes/get',
  async (jobId, api) => {
    try {
      const response = await makeRequest(getEndpoint(jobId), { method: 'GET' })(api);

      const ok = response.status === 200 || response.status === 201;
      if (!ok) {
        if (response.payload?.response?.data.errors) {
          return api.rejectWithValue({ errors: response.payload.response.data.errors });
        }

        return api.rejectWithValue({ serverError: `${response.payload?.response?.status}` });
      }

      return response.data.data;
    } catch (error) {
      return api.rejectWithValue({ error: error.message });
    }
  }
);

// Add New Note Thunk
export const addNewNote = createAsyncThunk<
  JSONApiDataObject<ExtendedNoteModel>,
  { jobId: string, text: string },
  { rejectValue: { errors?: Array<string>, serverError?: string, error?: string } }
>(
  'pro/outerJobNotes/addNew',
  async ({ jobId, text }, api) => {
    try {
      const response = await makeRequest(
        getEndpoint(jobId),
        {
          method: 'POST',
          body: {
            data: {
              type: 'outer-job-note',
              attributes: {
                body: text
              }
            }
          }
        }
      )(api);

      const ok = response.status === 200 || response.status === 201;
      if (!ok) {
        if (response.payload?.response?.data.errors) {
          return api.rejectWithValue({ errors: response.payload.response.data.errors });
        }

        return api.rejectWithValue({ serverError: `${response.payload?.response?.status}` });
      }

      return response.data.data;
    } catch (error) {
      return api.rejectWithValue({ error: error.message });
    }
  }
);

// Update Note Thunk
export const updateNote = createAsyncThunk<
  JSONApiDataObject<ExtendedNoteModel>,
  { jobId: string, note: ExtendedNoteModel },
  { rejectValue: { errors?: Array<string>, serverError?: string, error?: string } }
>(
  'pro/outerJobNotes/update',
  async ({ jobId, note }, api) => {
    try {
      const response = await makeRequest(
        `${getEndpoint(jobId)}${note.id}`,
        {
          method: 'PATCH',
          body: {
            data: {
              type: 'outer-job-note',
              id: note.id,
              attributes: {
                body: note.attributes.body
              }
            }
          }
        }
      )(api);

      const ok = response.status === 200 || response.status === 201;
      if (!ok) {
        if (response.payload?.response?.data.errors) {
          return api.rejectWithValue({ errors: response.payload.response.data.errors });
        }

        return api.rejectWithValue({ serverError: `${response.payload?.response?.status}` });
      }

      return response.data.data;
    } catch (error) {
      return api.rejectWithValue({ error: error.message });
    }
  }
);

// Delete Note Thunk
export const deleteNote = createAsyncThunk<
  string,
  { jobId: string, id: string },
  { rejectValue: { errors?: Array<string>, serverError?: string, error?: string } }
>(
  'pro/outerJobNotes/delete',
  async ({ jobId, id }, api) => {
    const { deleteNoteById } = outerJobNotesSlice.actions;
    await makeRequest(
      `${getEndpoint(jobId)}${id}`,
      {
        method: 'DELETE',
        body: {
          data: {
            type: 'outer-job-note',
            id
          }
        }
      }
    )(api);

    api.dispatch(deleteNoteById(id));
  }
);

export const outerJobNotesSlice = createSlice({
  name: 'outerJobNotes',
  initialState: notesAdapter.getInitialState({
    loading: false,
    error: false,
    meta: {},
    entities: {}
  } as ReduxEntity<JSONApiDataObject<ExtendedNoteModel>>),
  reducers: {
    updateNoteSlice: (state, action) => {
      notesAdapter.setMany(state, action.payload);
    },
    deleteNoteById: (state, action) => {
      notesAdapter.removeOne(state, action.payload);
    }
  },
  extraReducers: builder => {
    builder.addCase(getNotes.pending, state => {
      state.loading = true;
    });
    builder.addCase(getNotes.fulfilled, (state, action) => {
      notesAdapter.setAll(state, action.payload);
      state.loading = false;
    });
    builder.addCase(getNotes.rejected, (state, action) => {
      state.loading = false;
      state.error = true;
    });
    builder.addCase(addNewNote.fulfilled, (state, action) => {
      notesAdapter.addOne(state, action.payload);
    });
    builder.addCase(addNewNote.rejected, (state, action) => {
      state.error = true;
    });
    builder.addCase(updateNote.fulfilled, (state, action) => {
      notesAdapter.setOne(state, action.payload);
    });
    builder.addCase(updateNote.rejected, (state, action) => {
      state.error = true;
    });
    builder.addCase(deleteNote.rejected, (state, action) => {
      state.error = true;
    });
  }
});
